import { ButtonHTMLAttributes, forwardRef, ReactElement } from 'react'
import { Slot, Slottable } from '@radix-ui/react-slot'
import { cva, VariantProps } from 'class-variance-authority'

import { cn } from '@/lib/utils'

const buttonVariants = cva(
  [
    'group/button relative rounded-[0.5rem]',
    'flex space-x-[1.5rem] items-center justify-center',
    'before:absolute before:left-0 before:top-0 before:size-full before:rounded-[0.5rem] [&>*]:relative',
    'before:transition-colors before:duration-300 before:ease-in-out',
    'focus-visible:outline-none focus-visible:ring-2',
    'focus-visible:ring-[var(--focus-ring-dark)] dark:focus-visible:ring-[var(--focus-ring-light)]'
  ],
  {
    variants: {
      variant: {
        primary: [
          'dark:before:bg-white dark:text-dark before:bg-dark text-white',
          'hover:before:bg-[#333333] dark:hover:before:bg-light/70'
        ],
        secondary: [
          'before:border dark:before:border-white/45 before:border-black/25 text-dark dark:text-white',
          'hover:before:bg-light/70 dark:hover:before:bg-light/10'
        ],
        alert: [
          'bg-red-500 text-white',
          'hover:before:bg-red-600',
          'focus-visible:ring-red-900 dark:focus-visible:ring-red-900'
        ]
      },
      size: {
        default: 'min-w-[15rem] min-h-[5rem] py-[1.1rem] px-[2.4rem]',
        small: 'min-w-[0rem] min-h-[4.25rem] py-[1.1rem] px-[1.8rem]'
      }
    },
    defaultVariants: {
      variant: 'primary',
      size: 'default'
    }
  }
)

export type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean
    endIcon?: ReactElement | null
  }

const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ children, variant, size, endIcon, className, asChild = false, ...props }, ref) => {
    const Component = asChild ? Slot : 'button'

    return (
      <Component
        ref={ref}
        className={cn(buttonVariants({ variant, size, className }), '')}
        {...props}
      >
        <Slottable>{children}</Slottable>
        {endIcon}
      </Component>
    )
  }
)

export default Button
